import React from 'react';
import cx from 'classnames';
import { isAbsoluteUrl } from 'ha/utils/isAbsoluteUrl';
import { useIntl } from 'ha/i18n';

const isAnchorRef = (ref: string) => /^#/.test(ref);

export interface Props {
  to: string;
  className?: string;
  target?: '_blank' | '_self';
  title?: string;
  noFollow?: boolean;
  skipLangFormat?: boolean;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  itemScope?: boolean;
  itemType?: string;
  component?: React.ElementType;
  children?: React.ReactNode;
}

const Link = React.forwardRef<HTMLAnchorElement, Props>(
  (
    {
      to = '/',
      children,
      noFollow,
      className,
      target,
      skipLangFormat,
      component,
      ...rest
    },
    ref,
  ) => {
    const { urlResolver } = useIntl();
    const isTargetBlank = target === '_blank';

    const props = {
      className,
      target,
      rel: cx({
        nofollow: noFollow,
        noopener: isTargetBlank,
        noreferrer: isTargetBlank,
      }),
      ...rest,
    };

    const getHref =
      isAnchorRef(to) || isAbsoluteUrl(to) || skipLangFormat
        ? to
        : urlResolver.formatHrefURL(to);

    if (component) {
      const LinkComponent = component;

      return (
        <LinkComponent to={getHref} {...props} ref={ref}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <a href={getHref} {...props} ref={ref}>
        {children}
      </a>
    );
  },
);

export { Link };
